import React from 'react'
import SVGInline from '@/v2/components/common/SVGInline'
import * as Sentry from '@sentry/react'
import IconClearworks from '#/assets/images/icons/clearworks-logo.svg?raw'

class MainBoundary extends React.Component {
  state = {
    error: null,
    errorInfo: null,
    showDetails: false,
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo })
    console.error('Error caught by boundary:', error, errorInfo)

    Sentry.withScope(scope => {
      scope.setContext('componentStack', {
        stack: errorInfo.componentStack,
      })

      // The route the crash happened on — without this every MainBoundary issue
      // looks identical and we can't tell a reschedule crash from any other.
      scope.setContext('route', {
        url: window.location.href,
        pathname: window.location.pathname,
      })

      scope.setTag('error_boundary', 'MainBoundary')
      scope.setTag('route_pathname', window.location.pathname)
      scope.setLevel('error')

      let failedComponent = null
      if (errorInfo.componentStack) {
        const componentMatch = errorInfo.componentStack.match(/^\s+at\s+(\w+)/)
        if (componentMatch) {
          failedComponent = componentMatch[1]
          scope.setTag('failed_component', failedComponent)
        }
      }

      // Group by where it broke (component + route) rather than collapsing all
      // boundary catches into one giant issue.
      scope.setFingerprint([
        'MainBoundary',
        failedComponent || error?.name || 'unknown',
        window.location.pathname,
      ])

      Sentry.captureException(error)
    })
  }

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }))
  }

  renderErrorScreen() {
    const { showSVG = true } = this.props
    const { error, errorInfo, showDetails } = this.state

    const styles = {
      wrapper: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        padding: '20px',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      },
      card: {
        maxWidth: '600px',
        width: '100%',
        padding: '48px',
        textAlign: 'center',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
        borderRadius: '12px',
        background: 'white',
      },
      logo: {
        marginBottom: '32px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
      icon: {
        fontSize: '48px',
        marginBottom: '24px',
      },
      title: {
        fontSize: '28px',
        fontWeight: '600',
        color: '#111111',
        marginTop: '0',
        marginBottom: '16px',
      },
      text: {
        fontSize: '16px',
        color: '#7D7D7D',
        marginBottom: '8px',
        lineHeight: '1.5',
      },
      subtext: {
        fontSize: '14px',
        color: '#7D7D7D',
        marginBottom: '32px',
      },
      buttonGroup: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
        flexWrap: 'wrap',
      },
      buttonPrimary: {
        padding: '10px 24px',
        backgroundColor: '#0074FE',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        transition: 'background-color 0.2s',
      },
      buttonSecondary: {
        padding: '10px 24px',
        backgroundColor: 'white',
        color: '#0074FE',
        border: '1px solid #0074FE',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        textDecoration: 'none',
        display: 'inline-block',
        transition: 'background-color 0.2s',
      },
      detailsButton: {
        marginTop: '24px',
        padding: '8px 16px',
        background: 'none',
        color: '#7D7D7D',
        border: 'none',
        cursor: 'pointer',
        fontSize: '13px',
        textDecoration: 'underline',
      },
      errorDetails: {
        background: '#f5f5f5',
        borderRadius: '8px',
        padding: '16px',
        marginTop: '16px',
        textAlign: 'left',
        maxHeight: '200px',
        overflowY: 'auto',
        fontSize: '12px',
        color: '#666',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontFamily: 'monospace',
      },
    }

    return (
      <div style={styles.wrapper}>
        <div style={styles.card}>
          {showSVG && (
            <div style={styles.logo}>
              <SVGInline svg={IconClearworks} className="filled icon" />
            </div>
          )}

          <div style={styles.icon}>⚠️</div>

          <h1 style={styles.title}>Oops! Something went wrong</h1>

          <p style={styles.text}>
            We encountered an unexpected error while loading the application.
            Don't worry, your data is safe.
          </p>

          <p style={styles.subtext}>
            You can try refreshing the page or navigate back to the calendar.
          </p>

          <div style={styles.buttonGroup}>
            <button
              style={styles.buttonPrimary}
              onClick={() => window.location.reload()}
              onMouseEnter={e => (e.target.style.backgroundColor = '#0056b3')}
              onMouseLeave={e => (e.target.style.backgroundColor = '#0074FE')}
            >
              Refresh Page
            </button>
            <a
              href="/calendar"
              style={styles.buttonSecondary}
              onMouseEnter={e => (e.target.style.backgroundColor = '#f0f7ff')}
              onMouseLeave={e => (e.target.style.backgroundColor = 'white')}
            >
              Go to Calendar
            </a>
          </div>

          {error && (
            <>
              <button style={styles.detailsButton} onClick={this.toggleDetails}>
                {showDetails ? 'Hide' : 'Show'} Technical Details
              </button>

              {showDetails && (
                <div style={styles.errorDetails}>
                  {error.toString()}
                  {errorInfo && errorInfo.componentStack}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  render() {
    const { children } = this.props
    const { error } = this.state
    const hasError = !!error

    return hasError ? this.renderErrorScreen() : children
  }
}

export default MainBoundary
